import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { api } from "../lib/api";
import {
  X, FileSpreadsheet, Download, AlertCircle, CheckCircle2, AlertTriangle,
  ChevronRight, ChevronLeft, Trash2, FileX, Mail,
} from "lucide-react";

const SHEET_DEFS = {
  "Coaching staff": {
    suite: "Coach suite",
    cols: ["First name","Last name","Date of birth","Nationality","Role","Email address","Mobile number","Playmaker access level","Years experience","UEFA license"],
  },
  "Medical staff": {
    suite: "Medical suite",
    cols: ["First name","Last name","Date of birth","Nationality","Role","Email address","Mobile number","Playmaker access level","Specialisation","Medical license #"],
  },
  "Scouts": {
    suite: "Scouting suite",
    cols: ["First name","Last name","Date of birth","Nationality","Role","Email address","Mobile number","Playmaker access level","Region","Languages"],
  },
  "Support staff": {
    suite: "Support suite",
    cols: ["First name","Last name","Date of birth","Nationality","Role","Email address","Mobile number","Playmaker access level","Languages"],
  },
};

const STAFF_FIELD_MAP = {
  "First name": "first_name",
  "Last name": "last_name",
  "Date of birth": "date_of_birth",
  "Nationality": "nationality",
  "Role": "role",
  "Email address": "email_address",
  "Mobile number": "mobile_number",
  "Playmaker access level": "playmaker_access_level",
};

const ALLOWED_ACCESS = ["Full", "Standard", "Read-only"];
const MAX_BYTES = 10 * 1024 * 1024;

function parseDate(v) {
  if (!v && v !== 0) return null;
  if (v instanceof Date && !isNaN(v)) return v.toISOString().slice(0, 10);
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  if (typeof v === "number") {
    const dt = XLSX.SSF.parse_date_code(v);
    if (dt) return `${dt.y}-${String(dt.m).padStart(2, "0")}-${String(dt.d).padStart(2, "0")}`;
  }
  const d = new Date(s); if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return null;
}

function validateStaffRow(row, sheet) {
  const errors = [];
  const warnings = [];
  if (!row.first_name?.trim()) errors.push({ field: "First name", message: "Required — missing" });
  if (!row.last_name?.trim()) errors.push({ field: "Last name", message: "Required — missing" });
  if (!row.date_of_birth) errors.push({ field: "Date of birth", message: "Required — missing" });
  else {
    const p = parseDate(row.date_of_birth);
    if (!p) errors.push({ field: "Date of birth", message: `Cannot parse "${row.date_of_birth}"` });
    else row.date_of_birth = p;
  }
  if (!row.nationality?.trim()) errors.push({ field: "Nationality", message: "Required — missing" });
  if (!row.role?.trim()) errors.push({ field: "Role", message: "Required — missing" });
  if (!row.email_address) errors.push({ field: "Email address", message: "Required — missing" });
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email_address)) {
    errors.push({ field: "Email address", message: `"${row.email_address}" is not a valid email` });
  }
  if (!row.mobile_number?.trim()) errors.push({ field: "Mobile number", message: "Required — missing" });
  if (!row.playmaker_access_level) errors.push({ field: "Playmaker access level", message: "Required — missing" });
  else if (!ALLOWED_ACCESS.includes(row.playmaker_access_level)) {
    errors.push({ field: "Playmaker access level", message: `Must be one of ${ALLOWED_ACCESS.join(", ")}` });
  }
  row.sheet = sheet;
  return { errors, warnings };
}

const STATUS_TONE = {
  ready: { label: "Ready", cls: "pm-pill-green", icon: CheckCircle2 },
  warning: { label: "Warning", cls: "pm-pill-amber", icon: AlertTriangle },
  error: { label: "Error", cls: "pm-pill-red", icon: AlertCircle },
};

export default function BulkImportStaff({ open, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [rows, setRows] = useState([]); // { sheet, data, errors, warnings, status, skip }
  const [activeSheet, setActiveSheet] = useState("All");
  const [sendInvitations, setSendInvitations] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const reset = () => {
    setStep(1); setFile(null); setParsing(false); setParseError(null);
    setRows([]); setActiveSheet("All"); setBusy(false); setResult(null);
  };
  useEffect(() => { if (!open) setTimeout(reset, 300); }, [open]);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/import/staff/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "playmaker_staff_upload_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const parseFile = useCallback(async (f) => {
    setParseError(null); setParsing(true);
    try {
      if (f.size > MAX_BYTES) throw new Error(`File is ${(f.size / 1024 / 1024).toFixed(1)}MB — maximum is 10MB.`);
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      // Wrong-template detection — if Clients sheet exists, this is the client template
      if (wb.SheetNames.includes("Clients") && !wb.SheetNames.some((n) => Object.keys(SHEET_DEFS).includes(n))) {
        throw new Error("This looks like the client template. Please use the staff upload template.");
      }
      const all = [];
      for (const sheetName of Object.keys(SHEET_DEFS)) {
        if (!wb.SheetNames.includes(sheetName)) continue;
        const ws = wb.Sheets[sheetName];
        const grid = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: false });
        if (!grid.length) continue;
        const headers = grid[0].map((h) => String(h || "").trim());
        const dataRows = grid.slice(2).filter((arr) => arr.some((c) => c !== "" && c != null));
        for (let idx = 0; idx < dataRows.length; idx++) {
          const arr = dataRows[idx];
          const obj = {};
          headers.forEach((h, c) => {
            const key = STAFF_FIELD_MAP[h];
            if (key) obj[key] = arr[c] === "" ? null : arr[c];
          });
          const v = validateStaffRow(obj, sheetName);
          all.push({
            sheet: sheetName,
            row_number: idx + 1,
            data: obj,
            errors: v.errors,
            warnings: v.warnings,
            status: v.errors.length ? "error" : v.warnings.length ? "warning" : "ready",
            skip: false,
            expanded: false,
          });
        }
      }
      if (!all.length) throw new Error("No data rows found across any sheet. Make sure you've filled in at least one department.");
      setRows(all);
      setFile(f);
      setStep(2);
    } catch (e) {
      setParseError(e.message || "Could not parse file.");
      setFile(f);
    } finally { setParsing(false); }
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setDrag(false);
    const f = e.dataTransfer.files?.[0]; if (f) parseFile(f);
  }, [parseFile]);

  const toggleSkip = (idx) => setRows((all) => all.map((r, i) => i === idx ? { ...r, skip: !r.skip } : r));
  const toggleExpanded = (idx) => setRows((all) => all.map((r, i) => i === idx ? { ...r, expanded: !r.expanded } : r));
  const editField = (idx, key, value) => setRows((all) => all.map((r, i) => {
    if (i !== idx) return r;
    const data = { ...r.data, [key]: value };
    const v = validateStaffRow(data, r.sheet);
    const status = v.errors.length ? "error" : v.warnings.length ? "warning" : "ready";
    return { ...r, data, errors: v.errors, warnings: v.warnings, status };
  }));

  const filteredRows = useMemo(() => activeSheet === "All" ? rows : rows.filter((r) => r.sheet === activeSheet), [rows, activeSheet]);
  const summary = useMemo(() => {
    const active = rows.filter((r) => !r.skip);
    return {
      ready: active.filter((r) => r.status === "ready").length,
      warning: active.filter((r) => r.status === "warning").length,
      error: active.filter((r) => r.status === "error").length,
      skipped: rows.filter((r) => r.skip).length,
      total: rows.length,
      bySheet: Object.keys(SHEET_DEFS).reduce((acc, s) => ({ ...acc, [s]: rows.filter((r) => r.sheet === s).length }), {}),
    };
  }, [rows]);
  const importable = summary.ready + summary.warning;

  const doImport = async () => {
    setBusy(true);
    try {
      const sendRows = rows.filter((r) => !r.skip && r.status !== "error").map((r) => ({ ...r.data, sheet: r.sheet }));
      const res = await api.post("/import/staff", { rows: sendRows, send_invitations: sendInvitations });
      setResult(res.data);
      setStep(4);
      onSuccess?.(res.data);
    } catch (e) {
      setParseError(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm" data-testid="bulk-import-staff-modal">
      <div className="bg-white rounded-2xl w-full max-w-6xl h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Bulk import</div>
            <h2 className="font-display text-xl font-black tracking-tight">Bulk import staff</h2>
          </div>
          <Stepper step={step} />
          <button onClick={downloadTemplate} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="staff-download-template">
            <Download size={12} /> Download template
          </button>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 p-1" data-testid="staff-import-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-auto pm-scroll p-6">
          {step === 1 && (
            <Step1
              file={file} parsing={parsing} parseError={parseError} drag={drag}
              setDrag={setDrag} onDrop={onDrop} fileInputRef={fileInputRef}
              onFileChange={(e) => { const f = e.target.files?.[0]; if (f) parseFile(f); }}
              onDownloadTemplate={downloadTemplate} onRemove={reset}
            />
          )}
          {step === 2 && (
            <Step2
              summary={summary} rows={filteredRows} activeSheet={activeSheet} setActiveSheet={setActiveSheet}
              onToggleSkip={toggleSkip} onToggleExpanded={toggleExpanded} onEditField={editField}
            />
          )}
          {step === 3 && (
            <Step3 summary={summary} importable={importable} busy={busy}
                   sendInvitations={sendInvitations} setSendInvitations={setSendInvitations}
                   onBack={() => setStep(2)} onConfirm={doImport} />
          )}
          {step === 4 && result && <Step4 result={result} onAnother={reset} onClose={onClose} />}
        </div>

        {(step === 2 || step === 3) && (
          <div className="px-6 py-4 border-t border-slate-200 flex items-center gap-3">
            {step > 1 && step < 4 && (
              <button onClick={() => setStep((s) => Math.max(1, s - 1))} className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-2" data-testid="staff-back-btn">
                <ChevronLeft size={14} /> Back
              </button>
            )}
            <div className="flex-1" />
            {step === 2 && (
              <button onClick={() => setStep(3)} disabled={importable === 0} className="pm-btn-primary h-10 px-5 rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-40" data-testid="staff-continue-btn">
                Continue · {importable} ready <ChevronRight size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stepper({ step }) {
  const labels = ["Upload", "Review", "Confirm", "Done"];
  return (
    <div className="hidden md:flex items-center gap-2 text-[11px] font-bold" data-testid="staff-stepper">
      {labels.map((l, i) => {
        const idx = i + 1;
        const active = idx === step;
        const done = idx < step;
        return (
          <div key={l} className="flex items-center gap-2">
            <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[11px] ${
              done ? "bg-emerald-500 text-white" : active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"
            }`}>{done ? "✓" : idx}</span>
            <span className={active ? "text-slate-900" : "text-slate-500"}>{l}</span>
            {idx < labels.length && <span className="text-slate-300">›</span>}
          </div>
        );
      })}
    </div>
  );
}

function Step1({ file, parsing, parseError, drag, setDrag, onDrop, fileInputRef, onFileChange, onDownloadTemplate, onRemove }) {
  return (
    <div className="space-y-5">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        tabIndex={0}
        role="button"
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click(); }}
        className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
          drag ? "border-emerald-500 bg-emerald-50/40" : "border-slate-300 bg-slate-50/40 hover:border-slate-400"
        }`}
        data-testid="staff-upload-zone"
      >
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFileChange} data-testid="staff-file-input" />
        <div className="flex justify-center mb-3"><FileSpreadsheet size={42} className="text-slate-400" /></div>
        <div className="font-display text-lg font-bold tracking-tight">Drop your Playmaker staff template here</div>
        <div className="text-sm text-slate-500 mt-1">or click to browse</div>
        <div className="text-[11px] text-slate-400 mt-2 font-mono-tab">.xlsx · .xls · .csv · max 10 MB · 4 department sheets</div>
      </div>
      <div className="text-center text-sm">
        <span className="text-slate-500">Don't have the template? </span>
        <button onClick={onDownloadTemplate} className="font-bold text-emerald-700 hover:underline" data-testid="staff-step1-download-template">
          Download staff upload template →
        </button>
      </div>
      {file && (
        <div className="pm-card p-4 flex items-center gap-3" data-testid="staff-selected-file">
          <FileSpreadsheet size={18} className="text-emerald-700" />
          <div className="flex-1 min-w-0">
            <div className="font-bold truncate">{file.name}</div>
            <div className="text-[11px] text-slate-500 font-mono-tab">{(file.size / 1024).toFixed(1)} KB · {parsing ? "Parsing…" : "Ready"}</div>
          </div>
          <button onClick={onRemove} className="text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
        </div>
      )}
      {parseError && (
        <div className="pm-card p-4 flex items-start gap-3 border-l-4 border-l-red-400 bg-red-50/40" data-testid="staff-parse-error">
          <FileX size={18} className="text-red-700 mt-0.5 shrink-0" />
          <div className="text-sm text-slate-800">{parseError}</div>
        </div>
      )}
    </div>
  );
}

function Step2({ summary, rows, activeSheet, setActiveSheet, onToggleSkip, onToggleExpanded, onEditField }) {
  return (
    <div className="space-y-4">
      <div className="pm-card p-4 flex flex-wrap gap-3 items-center" data-testid="staff-summary">
        <SummaryChip label="Ready" value={summary.ready} tone="green" />
        <SummaryChip label="Warnings" value={summary.warning} tone="amber" />
        <SummaryChip label="Errors" value={summary.error} tone="red" />
        <SummaryChip label="Skipped" value={summary.skipped} tone="slate" />
        <span className="ml-auto text-[11px] text-slate-500 font-mono-tab">{summary.total} rows total</span>
      </div>

      {/* Sheet tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="staff-sheet-tabs">
        <SheetTab label="All" count={summary.total} active={activeSheet === "All"} onClick={() => setActiveSheet("All")} />
        {Object.entries(SHEET_DEFS).map(([s, def]) => (
          <SheetTab key={s} label={s} count={summary.bySheet[s] || 0} active={activeSheet === s} onClick={() => setActiveSheet(s)} sub={def.suite} />
        ))}
      </div>

      <div className="pm-card overflow-hidden">
        <div className="overflow-auto pm-scroll max-h-[55vh]">
          <table className="w-full text-sm" data-testid="staff-validation-table">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <Th>#</Th>
                <Th>Status</Th>
                <Th>Name</Th>
                <Th>Role</Th>
                <Th>Email</Th>
                <Th>Access</Th>
                <Th>Sheet</Th>
                <Th>Issues</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const tone = STATUS_TONE[r.status];
                const Icon = tone.icon;
                const realIdx = idx; // filtered idx ok because we toggle on the same data
                return (
                  <>
                    <tr key={idx} className={`border-t border-slate-100 ${r.skip ? "opacity-40" : ""}`} data-testid={`staff-row-${idx}`}>
                      <Td><span className="font-mono-tab text-xs text-slate-500">{r.row_number}</span></Td>
                      <Td><span className={`pm-pill ${tone.cls} text-[10px] inline-flex items-center gap-1`}><Icon size={10} /> {tone.label}</span></Td>
                      <Td><div className="font-bold text-sm text-slate-900 truncate">{r.data.first_name} {r.data.last_name}</div></Td>
                      <Td><span className="text-xs">{r.data.role || "—"}</span></Td>
                      <Td><span className="text-xs truncate">{r.data.email_address || "—"}</span></Td>
                      <Td><span className="text-xs">{r.data.playmaker_access_level || "—"}</span></Td>
                      <Td><span className="pm-pill pm-pill-slate text-[10px]">{r.sheet}</span></Td>
                      <Td>
                        {(r.errors.length + r.warnings.length) > 0 ? (
                          <button onClick={() => onToggleExpanded(realIdx)} className="text-[11px] font-bold text-slate-600 hover:text-slate-900 underline" data-testid={`staff-row-issues-${idx}`}>
                            {r.errors.length}E · {r.warnings.length}W
                          </button>
                        ) : <span className="text-[11px] text-emerald-700">—</span>}
                      </Td>
                      <Td>
                        <button onClick={() => onToggleSkip(realIdx)} className="text-[11px] font-bold text-slate-500 hover:text-red-700" data-testid={`staff-row-skip-${idx}`}>
                          {r.skip ? "Include" : "Skip"}
                        </button>
                      </Td>
                    </tr>
                    {r.expanded && (r.errors.length + r.warnings.length) > 0 && (
                      <tr key={idx + "-x"} className="bg-amber-50/30">
                        <td colSpan={9} className="px-4 py-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {[...r.errors, ...r.warnings].map((iss, i) => {
                              const fieldKey = STAFF_FIELD_MAP[iss.field] || iss.field;
                              return (
                                <div key={i} className="border border-amber-200 rounded-lg p-2 bg-white text-[11px]">
                                  <div className="font-bold text-slate-800">{iss.field}</div>
                                  <div className="text-amber-700">{iss.message}</div>
                                  <input
                                    defaultValue={r.data[fieldKey] || ""}
                                    onBlur={(e) => { if (e.target.value !== (r.data[fieldKey] || "")) onEditField(realIdx, fieldKey, e.target.value); }}
                                    className="pm-input mt-1 text-xs h-8"
                                    data-testid={`staff-fix-${idx}-${fieldKey}`}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Step3({ summary, importable, busy, sendInvitations, setSendInvitations, onBack, onConfirm }) {
  return (
    <div className="max-w-xl mx-auto py-4">
      <div className="pm-card p-8 text-center">
        <CheckCircle2 size={36} className="text-emerald-600 mx-auto" />
        <h3 className="font-display text-2xl font-black tracking-tight mt-3">Ready to import</h3>
        <div className="mt-6 space-y-2 text-sm text-slate-700 max-w-md mx-auto text-left">
          <Line label="Will be created" value={importable} tone="green" testid="staff-confirm-import-count" />
          <Line label="Skipped (errors / you marked them)" value={summary.error + summary.skipped} tone="slate" />
        </div>

        <div className="mt-4 pm-card p-3 max-w-md mx-auto text-left">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1.5">Department breakdown</div>
          {Object.entries(summary.bySheet).map(([s, n]) => (
            <div key={s} className="flex justify-between text-xs py-0.5">
              <span>{s} <span className="text-slate-500">→ {SHEET_DEFS[s].suite}</span></span>
              <span className="font-mono-tab font-bold">{n}</span>
            </div>
          ))}
        </div>

        <label className="mt-4 inline-flex items-center gap-2 text-xs cursor-pointer" data-testid="staff-invitations-toggle">
          <input type="checkbox" checked={sendInvitations} onChange={(e) => setSendInvitations(e.target.checked)} />
          <Mail size={12} /> Send invitation emails to each staff member after import (mocked)
        </label>

        <div className="mt-6 flex items-center justify-center gap-3">
          <button onClick={onBack} className="pm-btn-secondary h-11 px-5 rounded-xl text-sm font-bold">Back</button>
          <button onClick={onConfirm} disabled={busy || importable === 0} className="pm-btn-primary h-11 px-5 rounded-xl text-sm font-bold disabled:opacity-40" data-testid="staff-confirm-import-btn">
            {busy ? "Importing…" : `Import ${importable} staff`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Step4({ result, onAnother, onClose }) {
  if (result.rolled_back) {
    return (
      <div className="max-w-xl mx-auto py-4">
        <div className="pm-card p-8 text-center" data-testid="staff-rollback">
          <AlertCircle size={36} className="text-red-600 mx-auto" />
          <h3 className="font-display text-2xl font-black tracking-tight mt-3">Import rolled back</h3>
          <p className="text-sm text-slate-700 mt-4 max-w-md mx-auto">{result.reason}</p>
          <button onClick={onAnother} className="pm-btn-primary h-11 px-5 rounded-xl text-sm font-bold mt-6">Try again</button>
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-2xl mx-auto py-4">
      <div className="pm-card p-8" data-testid="staff-success">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center"><CheckCircle2 size={22} /></div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700">Success</div>
            <h3 className="font-display text-2xl font-black tracking-tight">{result.created} staff members added{result.invitations_queued ? ` · ${result.invitations_queued} invitations queued` : ""}</h3>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4">
          <Tile label="Created" value={result.created} />
          <Tile label="Skipped (existing)" value={result.skipped} />
          <Tile label="Errors" value={result.errors?.length || 0} />
        </div>
        {Object.keys(result.suite_summary || {}).length > 0 && (
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">By suite</div>
            <div className="pm-card divide-y divide-slate-100">
              {Object.entries(result.suite_summary).map(([suite, n]) => (
                <div key={suite} className="p-3 flex items-center gap-2 text-sm" data-testid={`staff-suite-${suite.replace(/\s+/g, "-").toLowerCase()}`}>
                  <span className="font-bold flex-1 truncate">{suite}</span>
                  <span className="pm-pill pm-pill-slate text-[10px]"><b className="font-mono-tab">{n}</b> created</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {result.invitations_queued > 0 && (
          <div className="mt-4 pm-card p-3 flex items-center gap-2 border-l-4 border-l-blue-400 bg-blue-50/40 text-sm text-slate-700" data-testid="staff-invitations-banner">
            <Mail size={14} className="text-blue-700" />
            <span><b>{result.invitations_queued}</b> invitation emails will be sent in the next few minutes (mocked).</span>
          </div>
        )}
        <div className="mt-6 flex items-center gap-3">
          <button onClick={onAnother} className="pm-btn-secondary h-11 px-5 rounded-xl text-sm font-bold">Import another file</button>
          <button onClick={onClose} className="pm-btn-primary h-11 px-5 rounded-xl text-sm font-bold flex-1" data-testid="staff-view-staff">View all staff</button>
        </div>
      </div>
    </div>
  );
}

/* ----- shared bits ----- */
function Th({ children }) { return <th className="text-left text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 px-4 py-3">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-3 align-top">{children}</td>; }
function SummaryChip({ label, value, tone }) {
  const cls = { green: "pm-pill-green", amber: "pm-pill-amber", red: "pm-pill-red", slate: "pm-pill-slate" }[tone];
  return <span className={`pm-pill ${cls} text-[11px] inline-flex items-center gap-1.5`} data-testid={`staff-summary-${tone}`}>{label} <b className="font-mono-tab">{value}</b></span>;
}
function Line({ label, value, tone, testid }) {
  const dot = tone === "green" ? "bg-emerald-500" : tone === "amber" ? "bg-amber-500" : "bg-slate-400";
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-2" data-testid={testid}>
      <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${dot}`} />{label}</span>
      <span className="font-mono-tab font-bold">{value}</span>
    </div>
  );
}
function Tile({ label, value }) {
  return (
    <div className="pm-card p-4 text-center">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label}</div>
      <div className="font-display text-3xl font-black mt-1">{value}</div>
    </div>
  );
}
function SheetTab({ label, count, active, onClick, sub }) {
  return (
    <button onClick={onClick} className={`px-4 h-10 text-xs font-bold border-b-2 transition-colors ${active ? "border-emerald-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
      {label} <span className={`ml-1 text-[10px] ${active ? "text-slate-500" : "text-slate-400"}`}>· {count}</span>
      {sub && <div className="text-[9px] uppercase tracking-[0.18em] text-slate-400 font-bold">{sub}</div>}
    </button>
  );
}
